import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RealtimeSubscription } from '@/lib/realtime';
import {
  subscribeToPlayerEvents,
  subscribeToGameDefinitions,
  subscribeToTeamPresets,
  subscribeToPlayerGroups,
  subscribeToSessionIndex,
} from '@/lib/realtime';
import { clearChannelStatuses, updateChannelStatus } from '@/lib/realtime-sync/channels';
import { enqueueGameDefinitionsSync } from '@/lib/realtime-sync/plugins/game-definitions';
import { enqueuePlayerEventsSync } from '@/lib/realtime-sync/plugins/player-events';
import { enqueuePlayerGroupsSync } from '@/lib/realtime-sync/plugins/player-groups';
import { enqueueSessionSync } from '@/lib/realtime-sync/plugins/session';
import { enqueueSessionIndexSync } from '@/lib/realtime-sync/plugins/session-index';
import { enqueueTeamPresetsSync } from '@/lib/realtime-sync/plugins/team-presets';
import type { UserId } from '@/schemas';

async function subscribeWithDiagnostics<T extends RealtimeSubscription>(
  channelKey: string,
  subscribe: () => Promise<T>,
  diagnosticsEnabled: boolean,
): Promise<T> {
  if (diagnosticsEnabled) {
    updateChannelStatus(channelKey, 'connecting');
  }

  try {
    const subscription = await subscribe();
    if (diagnosticsEnabled) {
      updateChannelStatus(channelKey, 'connected');
    }
    return subscription;
  } catch (error) {
    if (diagnosticsEnabled) {
      updateChannelStatus(channelKey, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
}

interface RealtimeState {
  subscriptions: Map<string, RealtimeSubscription>;
  isEnabled: boolean;
  currentUserId?: UserId;
}

interface RealtimeActions {
  enableRealtime: (userId: UserId, options?: { force?: boolean }) => Promise<void>;
  resetRealtime: (userId: UserId) => Promise<void>;
  disableRealtime: () => void;
}

type RealtimeStore = RealtimeState & RealtimeActions;

export const useRealtimeStore = create<RealtimeStore>()(
  devtools(
    (set, get) => ({
      subscriptions: new Map(),
      isEnabled: false,
      currentUserId: undefined,

      enableRealtime: async (userId: UserId, options?: { force?: boolean }) => {
        const { isEnabled, currentUserId, subscriptions } = get();
        const shouldForce = options?.force === true;
        if (isEnabled && !shouldForce && currentUserId === userId) {
          return;
        }

        try {
          // Tear down existing subscriptions before re-subscribing
          for (const subscription of subscriptions.values()) {
            subscription.unsubscribe();
          }

          const diagnosticsEnabled = true;

          const playerEventsSub = await subscribeWithDiagnostics(
            'player-events',
            () =>
              subscribeToPlayerEvents(userId, (event) => {
                const targetUserId = event.userId ?? userId;
                if (targetUserId) {
                  enqueuePlayerEventsSync(targetUserId);
                }
              }),
            diagnosticsEnabled,
          );

          const gameDefinitionsSub = await subscribeWithDiagnostics(
            'game-definitions',
            () =>
              subscribeToGameDefinitions(userId, () => {
                enqueueGameDefinitionsSync(userId);
              }),
            diagnosticsEnabled,
          );

          const teamPresetsSub = await subscribeWithDiagnostics(
            'team-presets',
            () =>
              subscribeToTeamPresets(userId, () => {
                enqueueTeamPresetsSync(userId);
              }),
            diagnosticsEnabled,
          );

          const playerGroupsSub = await subscribeWithDiagnostics(
            'player-groups',
            () =>
              subscribeToPlayerGroups(userId, () => {
                enqueuePlayerGroupsSync(userId);
              }),
            diagnosticsEnabled,
          );

          const sessionIndexSub = await subscribeWithDiagnostics(
            'session-index',
            () =>
              subscribeToSessionIndex(userId, (entry) => {
                enqueueSessionSync(entry.sessionId);
                enqueueSessionIndexSync({
                  sessionId: entry.sessionId,
                  userId,
                  options: {
                    createdAt: entry.createdAt,
                    updatedAt: entry.updatedAt,
                    deletedAt: entry.deletedAt,
                  },
                });
              }),
            diagnosticsEnabled,
          );

          set({
            subscriptions: new Map([
              ['player-events', playerEventsSub],
              ['game-definitions', gameDefinitionsSub],
              ['team-presets', teamPresetsSub],
              ['player-groups', playerGroupsSub],
              ['session-index', sessionIndexSub],
            ]),
            isEnabled: true,
            currentUserId: userId,
          });
        } catch (error: unknown) {
          const err = error instanceof Error ? error : new Error(String(error));
          console.error('Failed to enable realtime subscriptions', err);
          // Clean up any partial subscriptions
          get().disableRealtime();
        }
      },

      resetRealtime: async (userId: UserId) => {
        await get().enableRealtime(userId, { force: true });
      },

      disableRealtime: () => {
        const { subscriptions } = get();
        for (const subscription of subscriptions.values()) {
          subscription.unsubscribe();
        }
        clearChannelStatuses();
        set({
          subscriptions: new Map(),
          isEnabled: false,
          currentUserId: undefined,
        });
      },
    }),
    { name: 'RealtimeStore' },
  ),
);
